import React, { useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import BoldButton from '../../Common/BoldButton';
import {
  MergeFieldSelections,
  MergeTarget,
  PendingTransportSyncRecord,
} from '../../Common/dtos/PendingTransportSyncDto';
import { TransportDto } from '../../../requests/useGetTransports';
import { toAbbreviation } from '../../Common/Units';

interface PendingOperation {
  import: PendingTransportSyncRecord;
  mergeTarget?: MergeTarget;
  mergeFields?: MergeFieldSelections;
}

interface EditImportModalProps {
  visible: boolean;
  operation: PendingOperation;
  existingTransports: TransportDto[];
  onSave: (mergeFields: MergeFieldSelections) => void;
  onCreateNew?: () => void;
  onCancel: () => void;
}

type FieldSource = 'existing' | 'import';
type ImportAction = 'merge' | 'create';

interface FieldSelections {
  name: FieldSource;
  number: FieldSource;
  maxWeight: FieldSource;
  compartments: FieldSource;
}

const formatWeight = (value: number | undefined, unit: string | undefined): string => {
  const unitDisplay = unit ? toAbbreviation(unit) : '';
  if (value == null) return unitDisplay ? `- ${unitDisplay}` : '-';
  return unitDisplay ? `${value.toLocaleString()} ${unitDisplay}` : value.toLocaleString();
};

const formatCompartments = (compartments: { capacityUnit?: string; capacity?: number }[]): string => {
  if (!compartments?.length) return 'None';
  return compartments
    .map(c => `${c.capacity?.toLocaleString() ?? '?'} ${c.capacityUnit ? toAbbreviation(c.capacityUnit) : ''}`.trim())
    .join(', ');
};

const renderCompartmentsList = (compartments: { capacityUnit?: string; capacity?: number }[]): React.ReactNode => {
  if (!compartments?.length) return <span className="text-muted">None</span>;
  return (
    <ol className="mb-0 ps-3">
      {compartments.map((c, i) => (
        <li key={i}>{c.capacity?.toLocaleString() ?? '?'} {c.capacityUnit ? toAbbreviation(c.capacityUnit) : ''}</li>
      ))}
    </ol>
  );
};

export const getMergeChanges = (
  imp: PendingTransportSyncRecord,
  target: MergeTarget | undefined,
  mergeFields: MergeFieldSelections | undefined,
): { field: string; from: string; to: string }[] => {
  if (!target || !mergeFields) return [];

  const changes: { field: string; from: string; to: string }[] = [];

  if (mergeFields.updateName && imp.name !== target.name)
    changes.push({ field: 'Name', from: target.name, to: imp.name });
  if (mergeFields.updateNumber && (imp.number ?? '') !== (target.number ?? ''))
    changes.push({ field: 'Number', from: target.number ?? '', to: imp.number ?? '' });
  if (mergeFields.updateMaxWeight) {
    const from = formatWeight(target.maxWeight, target.weightUnit);
    const to = formatWeight(imp.maxWeight, imp.weightUnit);
    if (from !== to)
      changes.push({ field: 'Max Weight', from, to });
  }
  if (mergeFields.updateCompartments) {
    const from = formatCompartments(target.compartments);
    const to = formatCompartments(imp.compartments);
    if (from !== to)
      changes.push({ field: 'Compartments', from, to });
  }

  return changes;
};

interface FieldRowProps {
  field: keyof FieldSelections;
  label: string;
  existing: React.ReactNode;
  imported: React.ReactNode;
  isEqual: boolean;
  source: FieldSource;
  onChange: (field: keyof FieldSelections, source: FieldSource) => void;
}

const FieldRow = ({ field, label, existing, imported, isEqual, source, onChange }: FieldRowProps) => {
  if (isEqual) {
    return (
      <tr>
        <td className="fw-bold" style={{ width: '140px' }}>{label}</td>
        <td colSpan={2} className="text-muted">{existing}</td>
      </tr>
    );
  }

  return (
    <tr className="table-warning">
      <td className="fw-bold" style={{ width: '140px' }}>{label}</td>
      <td
        style={{ cursor: 'pointer', backgroundColor: source === 'existing' ? 'rgba(40, 167, 69, 0.2)' : undefined }}
        onClick={() => onChange(field, 'existing')}
        className={source === 'existing' ? 'border border-success border-2' : ''}
      >
        <div className="d-flex align-items-center">
          <Form.Check
            type="radio"
            name={`${field}-source`}
            checked={source === 'existing'}
            onChange={() => onChange(field, 'existing')}
            className="me-2"
          />
          <div>{existing}</div>
        </div>
      </td>
      <td
        style={{ cursor: 'pointer', backgroundColor: source === 'import' ? 'rgba(40, 167, 69, 0.2)' : undefined }}
        onClick={() => onChange(field, 'import')}
        className={source === 'import' ? 'border border-success border-2' : ''}
      >
        <div className="d-flex align-items-center">
          <Form.Check
            type="radio"
            name={`${field}-source`}
            checked={source === 'import'}
            onChange={() => onChange(field, 'import')}
            className="me-2"
          />
          <div>{imported}</div>
        </div>
      </td>
    </tr>
  );
};

const pickDefault = (existingEmpty: boolean, importEmpty: boolean): FieldSource =>
  existingEmpty && !importEmpty ? 'import' : 'existing';

const getInitialSelections = (
  imp: PendingTransportSyncRecord,
  target: MergeTarget | undefined,
  mergeFields: MergeFieldSelections | undefined,
): FieldSelections => {
  if (mergeFields) {
    return {
      name: mergeFields.updateName ? 'import' : 'existing',
      number: mergeFields.updateNumber ? 'import' : 'existing',
      maxWeight: mergeFields.updateMaxWeight ? 'import' : 'existing',
      compartments: mergeFields.updateCompartments ? 'import' : 'existing',
    };
  }
  return {
    name: pickDefault(!target?.name, !imp.name),
    number: pickDefault(!target?.number, !imp.number),
    maxWeight: pickDefault(target?.maxWeight == null, imp.maxWeight == null),
    compartments: pickDefault(!target?.compartments?.length, !imp.compartments?.length),
  };
};

const EditImportModal = ({ visible, operation, existingTransports, onSave, onCreateNew, onCancel }: EditImportModalProps) => {
  const target = operation.mergeTarget;
  const imp = operation.import;

  const [importAction, setImportAction] = useState<ImportAction>('merge');
  const [selections, setSelections] = useState<FieldSelections>(() => getInitialSelections(imp, target, operation.mergeFields));

  const handleSelectionChange = (field: keyof FieldSelections, source: FieldSource) => {
    setSelections(prev => ({ ...prev, [field]: source }));
  };

  const handleSave = () => {
    onSave({
      updateName: selections.name === 'import',
      updateNumber: selections.number === 'import',
      updateMaxWeight: selections.maxWeight === 'import',
      updateCompartments: selections.compartments === 'import',
    });
  };

  const nameConflict = existingTransports.find(t =>
    t.name.toLowerCase() === imp.name.toLowerCase()
  );

  const numberConflict = existingTransports.find(t =>
    !!t.number && !!imp.number && t.number === imp.number
  );

  return (
    <Modal show={visible} onHide={onCancel} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {importAction === 'merge'
            ? <>Resolve Merge: {imp.name} → {target?.name}</>
            : <>Create New Transport: {imp.name}</>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {importAction === 'merge' && (
          <>
            <p className="text-muted mb-3">
              Select a value for each field. Changes will sync back to TMX on import.
            </p>
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>
                    <span className="text-primary">Constellation Transport</span>
                    <div className="small text-muted fw-normal">(Keep current value)</div>
                  </th>
                  <th>
                    <span className="text-success">TMX Transport</span>
                    <div className="small text-muted fw-normal">(Use import value)</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <FieldRow
                  field="name"
                  label="Name"
                  existing={target?.name ?? '-'}
                  imported={imp.name}
                  isEqual={(target?.name ?? '') === imp.name}
                  source={selections.name}
                  onChange={handleSelectionChange}
                />
                <FieldRow
                  field="number"
                  label="Number"
                  existing={target?.number ?? '-'}
                  imported={imp.number ?? '-'}
                  isEqual={(target?.number ?? '') === (imp.number ?? '')}
                  source={selections.number}
                  onChange={handleSelectionChange}
                />
                <FieldRow
                  field="maxWeight"
                  label="Max Weight"
                  existing={formatWeight(target?.maxWeight, target?.weightUnit)}
                  imported={formatWeight(imp.maxWeight, imp.weightUnit)}
                  isEqual={formatWeight(target?.maxWeight, target?.weightUnit) === formatWeight(imp.maxWeight, imp.weightUnit)}
                  source={selections.maxWeight}
                  onChange={handleSelectionChange}
                />
                <FieldRow
                  field="compartments"
                  label="Compartments"
                  existing={renderCompartmentsList(target?.compartments ?? [])}
                  imported={renderCompartmentsList(imp.compartments)}
                  isEqual={formatCompartments(target?.compartments ?? []) === formatCompartments(imp.compartments)}
                  source={selections.compartments}
                  onChange={handleSelectionChange}
                />
              </tbody>
            </table>
            {onCreateNew && (
              <>
                <hr />
                <BoldButton className="btn-ghost-secondary btn-sm" onClick={() => setImportAction('create')}>
                  Create new transport instead
                </BoldButton>
              </>
            )}
          </>
        )}

        {importAction === 'create' && (
          <>
            <button
              type="button"
              className="btn btn-link btn-sm text-muted ps-0 mb-3"
              onClick={() => setImportAction('merge')}
            >
              <i className="bi-arrow-left me-1" />Back to merge settings
            </button>

            {(nameConflict || numberConflict) && (
              <div className="alert alert-warning small py-2 mb-3">
                <i className="bi-exclamation-triangle-fill me-2" />
                {nameConflict && <>A transport named <strong>{nameConflict.name}</strong> already exists in Constellation.</>}
                {numberConflict && <>Transport number <strong>{numberConflict.number}</strong> is already in use.</>}
                {' '}Importing may cause confusion — consider merging instead.
              </div>
            )}

            <p className="text-muted">
              A new transport will be created in Constellation using the TMX values below.
            </p>
            <table className="table table-bordered">
              <tbody>
                <tr><td className="fw-bold" style={{ width: '140px' }}>Name</td><td>{imp.name || '-'}</td></tr>
                <tr><td className="fw-bold">Number</td><td>{imp.number || '-'}</td></tr>
                <tr><td className="fw-bold">Max Weight</td><td>{formatWeight(imp.maxWeight, imp.weightUnit)}</td></tr>
                <tr><td className="fw-bold">Compartments</td><td>{formatCompartments(imp.compartments)}</td></tr>
              </tbody>
            </table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <BoldButton className="btn-ghost-secondary" onClick={onCancel}>
          Cancel
        </BoldButton>
        {importAction === 'merge' ? (
          <BoldButton className="btn-primary" onClick={handleSave}>
            Save Settings
          </BoldButton>
        ) : (
          <BoldButton className="btn-primary" onClick={onCreateNew}>
            Confirm — Create New
          </BoldButton>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export { EditImportModal };
